import {
  AnimalInputCode,
  GeneralToolCode,
  GreenhouseCode,
  GrowingMediaCode,
  IInputUnit,
  ToolInputCode,
  UnitTitle,
  UnitValue,
  WaterEquipmentCode,
} from '../unitGenerator/unit.types';

interface IFilterToolsHelper {
  subInputCode: number;
  inputCode: number;
}

interface IToolUnitRule {
  /** Explicit unit list per sub-input code. */
  subInputs?: Partial<Record<number, readonly IInputUnit[]>>;
  /** Used when there is no sub-input entry (or the input has no sub-inputs). */
  fallback: readonly IInputUnit[];
}

// ---------------------------------------------------------------------------
// Building blocks (created once at module load)
// ---------------------------------------------------------------------------

const createUnit = (value: UnitValue, title: UnitTitle): Readonly<IInputUnit> =>
  Object.freeze({value, title});

const unitSet = (...units: IInputUnit[]): readonly IInputUnit[] =>
  Object.freeze(units);

const UNIT = {
  GRAM: createUnit(UnitValue.GRAM, UnitTitle.GRAM),
  KILOGRAM: createUnit(UnitValue.KILOGRAM, UnitTitle.KILOGRAM),
  TON: createUnit(UnitValue.TON, UnitTitle.TON),
  LITER: createUnit(UnitValue.LITER, UnitTitle.LITER),
  MILLILITER: createUnit(UnitValue.MILLILITER, UnitTitle.MILLILITER),
  METER: createUnit(UnitValue.METER, UnitTitle.METER),
  NUMBER: createUnit(UnitValue.NUMBER, UnitTitle.NUMBER),
  BOX: createUnit(UnitValue.BOX, UnitTitle.BOX),
  TRUNK: createUnit(UnitValue.TRUNK, UnitTitle.TRUNK),
  HEAD: createUnit(UnitValue.HEAD, UnitTitle.HEAD),
} as const;

// Unit sets shared by several entries (order = display order)
const NUMBER_ONLY = unitSet(UNIT.NUMBER);
const METER_ONLY = unitSet(UNIT.METER);
const BOX_NUMBER = unitSet(UNIT.BOX, UNIT.NUMBER);
const NUMBER_BOX = unitSet(UNIT.NUMBER, UNIT.BOX);
const METER_BOX = unitSet(UNIT.METER, UNIT.BOX);
const METER_NUMBER = unitSet(UNIT.METER, UNIT.NUMBER);
const METER_BOX_NUMBER = unitSet(UNIT.METER, UNIT.BOX, UNIT.NUMBER);
const LITER_NUMBER = unitSet(UNIT.LITER, UNIT.NUMBER);

// ---------------------------------------------------------------------------
// Config: ToolInputCode → sub-input code → unit list
// ---------------------------------------------------------------------------

const TOOL_UNITS_CONFIG: Record<number, IToolUnitRule> = {
  [ToolInputCode.GENERAL_TOOLS]: {
    subInputs: {
      [GeneralToolCode.GARDENING_GLUE]: unitSet(
        UNIT.GRAM,
        UNIT.KILOGRAM,
        UNIT.LITER,
        UNIT.NUMBER,
      ),
      [GeneralToolCode.INSECTICIDE_GLUE]: BOX_NUMBER,
      [GeneralToolCode.DRIP_IRRIGATION_TAPE]: METER_BOX_NUMBER,
      [GeneralToolCode.GREENHOUSE_SHADE_NET]: METER_BOX_NUMBER,
      [GeneralToolCode.GREENHOUSE_NYLON]: METER_BOX_NUMBER,
    },
    fallback: NUMBER_ONLY,
  },

  [ToolInputCode.TIE_STRAPS]: {fallback: NUMBER_ONLY},

  [ToolInputCode.PRUNING_TOOLS]: {fallback: NUMBER_ONLY},

  [ToolInputCode.WATER_EQUIPMENT]: {
    subInputs: {
      [WaterEquipmentCode.IRRIGATION_HOSE]: METER_ONLY,
      [WaterEquipmentCode.DRIP_IRRIGATION_PARTS]: METER_NUMBER,
      [WaterEquipmentCode.TANK]: LITER_NUMBER,
      [WaterEquipmentCode.PIPE]: METER_NUMBER,
      [WaterEquipmentCode.MOTOR_PUMP]: unitSet(UNIT.NUMBER),
    },
    fallback: NUMBER_ONLY,
  },

  [ToolInputCode.GROWING_MEDIA]: {
    subInputs: {
      [GrowingMediaCode.SEEDLING_TRAY]: BOX_NUMBER,
    },
    fallback: unitSet(
      UNIT.BOX,
      UNIT.GRAM,
      UNIT.KILOGRAM,
      UNIT.LITER,
      UNIT.TON,
      UNIT.MILLILITER,
    ),
  },

  [ToolInputCode.GREENHOUSE]: {
    subInputs: {
      [GreenhouseCode.MEASURING_TOOLS]: NUMBER_BOX,
      [GreenhouseCode.FITTINGS]: NUMBER_BOX,
      [GreenhouseCode.COVERING_TYPES]: METER_BOX,
      [GreenhouseCode.NETTING]: METER_BOX,
      [GreenhouseCode.CONSUMABLES]: NUMBER_BOX,
    },
    fallback: NUMBER_ONLY,
  },

  [ToolInputCode.ANIMAL_INPUTS]: {
    subInputs: {
      [AnimalInputCode.SUPPLEMENTS_AND_MICRONUTRIENTS]: unitSet(
        UNIT.GRAM,
        UNIT.KILOGRAM,
        UNIT.LITER,
        UNIT.MILLILITER,
        UNIT.TON,
        UNIT.NUMBER,
        UNIT.BOX,
      ),
      [AnimalInputCode.POULTRY_CONCENTRATE]: unitSet(
        UNIT.KILOGRAM,
        UNIT.LITER,
        UNIT.TON,
        UNIT.BOX,
        UNIT.METER,
        UNIT.TRUNK,
      ),
    },
    fallback: unitSet(UNIT.KILOGRAM, UNIT.TON, UNIT.BOX),
  },

  [ToolInputCode.LIVESTOCK_FARMING]: {
    fallback: unitSet(UNIT.NUMBER, UNIT.HEAD),
  },
};

// ---------------------------------------------------------------------------
// Lookup
// ---------------------------------------------------------------------------

export function filterToolsInputGroups({
  subInputCode,
  inputCode,
}: IFilterToolsHelper): IInputUnit[] {
  const rule = TOOL_UNITS_CONFIG[inputCode];
  const units = rule?.subInputs?.[subInputCode] ?? rule?.fallback;

  // Shallow copy so callers still get a fresh, mutable array like before.
  return [...units];
}
